import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { MongoDBDatalayer } from '@adornis/mongodb/server/MongoDBDatalayer.js';
import { MongoDBDatalayerHookAction } from '@adornis/mongodb/server/MongoDBHooks.js';
import './api/BookingAPI.js';
import './api/ClassBookingFilterAspect.js';
import './entities/Booking.js';
import { Booking } from './entities/Booking.js';
import './entities/ClassBooking.js';
import type { ClassBooking } from './entities/ClassBooking.js';
import './entities/DonationBooking.js';
import { EmployeeBooking } from './entities/EmployeeBooking.js';
import { bookingHashHook } from './server/hooks/bookingHashHook.js';
import { bookingMandateRuntimeHook } from './server/hooks/bookingMandateRuntimeHook.js';
import { bookingSchoolyearHook } from './server/hooks/bookingSchoolyearHook.js';

export async function initalizeBookings(mongoDatalayer: MongoDBDatalayer): Promise<void> {
  // the collection holds every booking class, so it is typed as the widest of them
  const col = mongoDatalayer.collection<EntityData<ClassBooking>>(Booking._collectionName);
  await col.createIndex({ userID: 1 });
  await col.createIndex({ hash: 1 });
  await col.createIndex({ bookingReference: 1 });
  /**
   * One employee booking per contract - enforced by the database because `syncEmployeeBookings` cannot do it alone: it
   * reads the person's bookings, then creates the missing ones, so two syncs of the same person at once both find no
   * booking for a new contract and both create one. That happens whenever a contract and its supplement are saved
   * together (each runs a sync from its hook), or a hook and the permission sync worker meet. The second insert fails
   * on this index, and `syncEmployeeBookings` runs again on what the first one wrote.
   *
   * Partial: only employee bookings carry a contract, and the booking of before without one is not counted.
   */
  await col.createIndex(
    { contractID: 1 },
    {
      unique: true,
      name: 'employee-booking-contract-unique',
      partialFilterExpression: { _class: EmployeeBooking._class, contractID: { $type: 'string' } },
    },
  );

  /**
   * Register the Booking hooks before the hash as they are executed in register order
   */
  col.registerHook(MongoDBDatalayerHookAction.PRE_INSERT, bookingSchoolyearHook.preInsert);
  col.registerHook(MongoDBDatalayerHookAction.PRE_REPLACE, bookingSchoolyearHook.preReplace);
  col.registerHook(MongoDBDatalayerHookAction.POST_UPDATE, bookingSchoolyearHook.postUpdate);

  col.registerHook(MongoDBDatalayerHookAction.PRE_INSERT, bookingHashHook.preInsert);
  col.registerHook(MongoDBDatalayerHookAction.PRE_REPLACE, bookingHashHook.preReplace);
  col.registerHook(MongoDBDatalayerHookAction.POST_UPDATE, bookingHashHook.postUpdate);

  col.registerHook(MongoDBDatalayerHookAction.POST_UPDATE, bookingMandateRuntimeHook.postUpdate);
  col.registerHook(MongoDBDatalayerHookAction.POST_REPLACE, bookingMandateRuntimeHook.postReplace);
}
